import { CREATURE_SIZE_TYPES } from './creature-size-types';
import { pcPictureRow } from './pc-picture-pair';
/** Stateless token geometry, shared by drawing and gameplay. */
import { picturesOf, itemPictures, itemsCol, sizeIdOf, catalogIdentity, type PictureField, type EncounterCreatureRow } from './array-types';
import { readCreatureStatuses } from '../janus-engine/compile/creature-status-reader';
import { statusPaused, statusRows } from '../janus-engine/statuses/expiry';
import { getSwarmTemplateForCreature, swarmWidthLanesForCreature, swarmOccupiedWidthFt, swarmVRatio01 } from './swarm-templates';

type CreatureBlob = Record<string, unknown>;
const recordOf = (v: unknown): CreatureBlob | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as CreatureBlob : null;
const pictureOf = (v: unknown): PictureField | null => recordOf(v) as PictureField | null;
const first = (v: unknown): unknown => Array.isArray(v) ? v[0] : undefined;
export interface CreaturePictureOptions { use_picture_thumbnail?: boolean }
export interface CreaturePictureReaders {
    pictures: (row: unknown, key: string) => unknown[];
    itemPictures: (row: unknown) => unknown[];
    items: (row: unknown, key: string) => unknown;
    statuses: (row: unknown, mode: 'target') => Record<string, unknown>[];
    paused: (row: Record<string, unknown>) => boolean;
    picture: (row: unknown, key: string) => any;
}
/** The page's pictureField0 aliases; URL values are already canonical scalar fields. */
export const creaturePictureField = (value: unknown, key: string): any => {
    const row = recordOf(value);
    if (key === 'pic_url' || key === 'picture_url') return row?.picture_url;
    return key === 'pic_thumbnail' ? row?.picture_thumbnail ?? row?.pic_thumbnail : row?.[key];
};
export const defaultPictureReaders: CreaturePictureReaders = {
    pictures: picturesOf, itemPictures, items: itemsCol,
    // Appearance is read-only: hydrate sparse definitions without cloning a creature's entire status tree.
    statuses: value => {
        const rows = statusRows(recordOf(value), 'target').filter(status => {
            const ref = status.status_item_id ?? status.status_arr;
            const identity = !recordOf(status.status_arr) && typeof ref === 'string' ? catalogIdentity(value, ref) : null;
            return String(itemsCol(status, 'item_type') ?? itemsCol(identity, 'item_type') ?? '').toLowerCase() === 'creature';
        });
        const sparse = rows.filter(status => !recordOf(status.status_arr));
        if (!sparse.length) return rows;
        // The canonical status reader resolves the riding catalog directly. A general full-tree
        // reference search would index the whole encounter once for every freshly projected body.
        const hydrated = Object.values(readCreatureStatuses({ catalog: recordOf(value)?.catalog, statuses: { target: sparse } }));
        let next = 0;
        return rows.map(status => recordOf(status.status_arr) ? status : recordOf(hydrated[next++]) ?? status);
    },
    paused: statusPaused, picture: creaturePictureField,
};

export function selectCreaturePicture(value: unknown, { use_picture_thumbnail = true }: CreaturePictureOptions = {}, read: CreaturePictureReaders = defaultPictureReaders): PictureField | null {
    const { pictures: ftPicturesOf, itemPictures: ftItemPictures, items: ftItemsCol, statuses: ftCreatureStatuses, paused: statusEffectInstanceIsCampaignPaused, picture: pictureField0 } = read;
    const c = recordOf(value);
    if (!c) return null;
    let pic: PictureField | null = null;

    // === 1. SHAPESHIFTED CREATURE (MUST BE FIRST!) ===
    // Monster-template shapeshift: the form's real appearance rides the template's creature_model
    // gallery (bare Canvas has only its placeholder). check_shapeshifts_for_item_arr bakes the chosen
    // model picture (seeded-random by status id) onto computed.creature.shapeshift_pic because the
    // status_arr collapses to a bare Canvas ref in the cache. Prefer it — mirror of PHP
    // get_object_picture_arr §1 so the token's picture + shape follow the man, not Canvas.
    const forms = ftCreatureStatuses(c, 'target').filter(eff =>
        !statusEffectInstanceIsCampaignPaused(eff) && ftItemsCol(eff, 'item_type') === 'creature');
    const shiftFlag = Array.isArray(c.shapeshifted) ? first(c.shapeshifted) : c.shapeshifted;
    const isShapeshifted = Number(shiftFlag ?? 0) === 1 || forms.length > 0;
    if (isShapeshifted) {
        const baked = recordOf(recordOf(c.computed)?.creature)?.shapeshift_pic;
        if (baked && pictureField0(baked, 'pic_url')) {
            pic = pictureOf(baked);
        }
    }
    const form = forms[0]?.status_arr;
    if (!pic && form) {
        // Use the form's gallery only; a second creature status cannot lend it another look.
        const candidate = [ftItemPictures(form)[0], ...[
            'encounter_creature_picture', 'encounter_creature_presentation_picture', 'encounter_creature_thumbnail_picture',
        ].map(key => ftPicturesOf(form, key)[0])].find(entry => pictureField0(entry, 'pic_url'));
        pic = pictureOf(candidate);
    }
    // A form with no art gets the generic token. The original character is never its fallback.
    if (isShapeshifted) return pic;

    // === 2. pic_url from roll === (roll snapshot uses the legacy pic_url key; wrap as
    // picture_url so downstream pictureField0 reads resolve it post-rename)
    const rollPicUrl = first(c.pic_url);
    if (!pic && rollPicUrl) {
        pic = { picture_url: String(rollPicUrl) };
    }

    // === 3. encounter_creature_picture (instance gallery — must win over template pointers) ===
    if (!pic) {
        const _ecPics = ftPicturesOf(c.encounter_creatures, 'encounter_creature_picture');
        if (_ecPics[0] && pictureField0(_ecPics[0], 'pic_url')) {
            pic = pictureOf(_ecPics[0]);
        }
    }

    // === 4–5. Appearance stamps: compact views prefer the GM's thumbnail; full views prefer XZ art. ===
    // ROUND-9 #9: member-root call — ftPicturesOf's EC-stamp bridge reads kept stamps from the
    // nested ec bucket and re-derives ELIDED ones from the pointer id against the own galleries.
    const stampTypes = use_picture_thumbnail
        ? ['encounter_creature_thumbnail_picture', 'encounter_creature_presentation_picture']
        : ['encounter_creature_presentation_picture', 'encounter_creature_thumbnail_picture'];
    for (const type of stampTypes) {
        if (pic) break;
        const stamps = ftPicturesOf(c, type);
        if (stamps[0] && pictureField0(stamps[0], 'pic_url')) {
            pic = pictureOf(stamps[0]);
        }
    }

    // === 6. PLAYER CHARACTER PICTURE — INTELLIGENT SEARCH ===
    const _pcPics: unknown[] = ftPicturesOf(c.player_characters, 'player_character_picture');
    if (!pic && _pcPics.length > 0) {
        pic = pictureOf(pcPictureRow(_pcPics, use_picture_thumbnail ? 'avatar' : 'full'));
    }

    // === 7. item_picture fallback — CREATURES ONLY ===
    // ftItemPictures' carrier walk descends the default-provider slots, and since ROUND-5 #5
    // (pc provider slots = resolvable bare refs) that walk reaches a PC's ancestry/class defs —
    // so an upload-less PC inherited the generic ancestry art (Artin Goblinchaser → Giantfolk
    // portrait) instead of falling through to initials. A PC's art is uploads (step 6) or the
    // shapeshift form (step 1), never a provider item gallery; creatures keep the inheritance
    // (a Black Dragon legitimately wears the Black Dragons classification art).
    if (!pic && !c.player_characters) {
        const _cPicsForFallback = ftItemPictures(c);
        if (_cPicsForFallback.length > 0 && pictureField0(_cPicsForFallback[0], 'pic_url')) {
            pic = pictureOf(_cPicsForFallback[0]);
        }
    }

    return pic; // null → use initials
}


/** Exactly the thumbnail's pixel rounding and stepped swarm frontage. */
export function creatureTokenGeometry(row: CreatureBlob, pic: unknown,
    sizes: readonly { item_id: string; base_size: number; thumbnail_width: number; variable_name: string }[] = CREATURE_SIZE_TYPES,
    sizeId: unknown = sizeIdOf(row), picture: CreaturePictureReaders['picture'] = creaturePictureField) {
    const sizeInfo = sizes.find(s => s.item_id === (sizeId ?? '13456FEC-A2EE-DB46-5829-0EB2F627E1B7')) || sizes[0]!;
    const thumbW = sizeInfo.thumbnail_width, baseFt = sizeInfo.base_size, sizeName = sizeInfo.variable_name;
    let pixelH = 0, heightFt = baseFt;
    const pcW = picture(pic, 'picture_width'), pcH = picture(pic, 'picture_height');
    if (pic && pcW && pcH) {
        pixelH = Math.round(pcH / pcW * thumbW);
        heightFt = +(pixelH / thumbW * baseFt).toFixed(2);
    }
    if (!pixelH) pixelH = thumbW;
    let tokenW = thumbW, widthFt = baseFt;
    const swarmDef = getSwarmTemplateForCreature(row as unknown as EncounterCreatureRow);
    if (swarmDef) {
        const lanes = swarmWidthLanesForCreature(row as unknown as EncounterCreatureRow, swarmDef);
        const occFt = swarmOccupiedWidthFt(baseFt, swarmDef, lanes, swarmVRatio01(row as unknown as EncounterCreatureRow));
        if (occFt > baseFt) {
            widthFt = occFt;
            tokenW = Math.round(thumbW * occFt / baseFt);
        }
    }
    return { thumbW, baseFt, sizeName, pixelH, heightFt, tokenW, widthFt };
}
